/**
 * MUI Chip recipes — Figma "Tags & Icons" (node-id=30948-214492).
 * https://www.figma.com/design/buw5Jv2ttgSveHoAz4pzMy/AIVO---Client-Portal?node-id=30948-214492
 *
 * Mapping from Figma to the `Chip` `color`/`variant` props. The `MUI/Chip`
 * Storybook stories ("Pill tags" / "Square tags") mirror these matrices.
 *
 * circle-tag (pill) — use `variant="pill"`:
 *   red → error · orange → orange · yellow → yellow (distinct from warning) ·
 *   green → success · light-blue → lightBlue (#A6C0DC) · dark-blue → darkBlue ·
 *   pink → pink · Violet → violet · border → borderSubtle (paper + border) ·
 *   grey → default · Filled (brand) → primary.
 *   Sizes: Figma default/small → Chip size medium/small (~24px / ~16px min-height).
 *
 * square-tag (filters) — Off: variant="outlined"; On: variant="filled"; same color:
 *   grey → default · green → success · red → error · orange → orange ·
 *   yellow → yellow · dark blue → darkBlue · violet → violet · pink → pink ·
 *   light blue → lightBlue · white → whiteOutlined.
 *
 * Soft semantic extras used in-app but outside the base Figma matrix:
 *   brandLight, successLight, orangeLight.
 */
import type { ChipProps } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { primitives, tokens } from './tokens';

type ChipColorKey = NonNullable<ChipProps['color']>;
type ChipVariantKey = NonNullable<ChipProps['variant']>;

export type ChipVariantRecipe = {
  backgroundColor: string;
  color: string;
  borderColor?: string;
  border?: string;
  boxShadow?: string;
  deleteIconColor?: string;
  iconColor?: string;
  hoverBackgroundColor?: string;
};

type ChipColorRecipe = {
  filled: ChipVariantRecipe;
  outlined?: ChipVariantRecipe;
  pill?: ChipVariantRecipe;
  pillBordered?: ChipVariantRecipe;
  square?: ChipVariantRecipe;
  squareBordered?: ChipVariantRecipe;
  squareActive?: ChipVariantRecipe;
};

export type FigmaTagChipColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'lightBlue'
  | 'darkBlue'
  | 'pink'
  | 'violet'
  | 'grey'
  | 'borderSubtle'
  | 'whiteOutlined';

type FigmaTagColorRecipe = {
  label: string;
  figmaName: string;
  color: FigmaTagChipColor;
  base: string;
  text: string;
  activeText: string;
  soft: string;
  medium?: string;
  iconColor?: string;
};

const textOnWhite = tokens.text.primary;
const white = '#fff';

const tagColor = ({
  label,
  figmaName,
  color,
  base,
  text = base,
  activeText = white,
  soft = alpha(base, 0.2),
  medium = alpha(base, 0.5),
  iconColor,
}: {
  label: string;
  figmaName: string;
  color: FigmaTagChipColor;
  base: string;
  text?: string;
  activeText?: string;
  soft?: string;
  medium?: string;
  iconColor?: string;
}): FigmaTagColorRecipe => ({
  label,
  figmaName,
  color,
  base,
  text,
  activeText,
  soft,
  medium,
  iconColor,
});

export const FIGMA_TAG_CHIP_COLORS = [
  tagColor({
    label: 'Red',
    figmaName: 'Red/Red 100%',
    color: 'red',
    base: primitives.red[100],
    medium: primitives.red[50],
    text: primitives.red[175],
  }),
  tagColor({
    label: 'Orange',
    figmaName: 'Orange/Orange 100%',
    color: 'orange',
    base: primitives.orange[100],
    medium: alpha(primitives.orange[50], 0.5),
    text: primitives.orange[150],
  }),
  tagColor({
    label: 'Yellow',
    figmaName: 'Yellow/Yellow 100%',
    color: 'yellow',
    base: primitives.yellow[100],
    text: primitives.yellow[150],
    medium: alpha(primitives.yellow[25], 0.5),
    activeText: primitives.yellow[150],
  }),
  tagColor({
    label: 'Green',
    figmaName: 'Green/Green 100',
    color: 'green',
    base: primitives.green[100],
    text: primitives.green[175],
    medium: alpha(primitives.green[25], 0.5),
  }),
  tagColor({
    label: 'Light blue',
    figmaName: 'Light Blue',
    color: 'lightBlue',
    base: primitives.brand[100],
    text: primitives.brand[150],
    medium: primitives.brand[50],
  }),
  tagColor({
    label: 'Dark blue',
    figmaName: 'Dark Blue/Dark Blue',
    color: 'darkBlue',
    base: primitives.darkBlue[100],
    medium: primitives.darkBlue[25],
    text: primitives.darkBlue[150],
  }),
  tagColor({
    label: 'Pink',
    figmaName: 'Pink/Pink 100%',
    color: 'pink',
    base: primitives.pink[125],
    text: primitives.pink[175],
    medium: alpha(primitives.pink[50], 0.5),
  }),
  tagColor({
    label: 'Violet',
    figmaName: 'Violet/Violet',
    color: 'violet',
    base: primitives.purple[100],
    text: primitives.purple[150],
    medium: alpha(primitives.purple[25], 0.5),
  }),
  tagColor({
    label: 'Pink',
    figmaName: 'Pink/Pink 100%',
    color: 'pink',
    base: primitives.pink[125],
    text: primitives.pink[175],
    medium: alpha(primitives.pink[50], 0.5),
  }),
  tagColor({
    label: 'Grey',
    figmaName: 'Neutrals/Solid/0',
    color: 'grey',
    base: primitives.neutrals.solid[0],
    text: primitives.neutrals.solid[0],
    iconColor: primitives.neutrals.solid[80],
    medium: primitives.neutrals.solid[100],
  }),
  tagColor({
    label: 'Border',
    figmaName: 'Neutrals/Solid/90 border',
    color: 'borderSubtle',
    base: tokens.border.main,
    text: textOnWhite,
    activeText: textOnWhite,
    soft: tokens.background.paper,
    medium: tokens.grey[100],
  }),
  tagColor({
    label: 'White',
    figmaName: 'White',
    color: 'whiteOutlined',
    base: primitives.neutrals.solid[20],

    text: textOnWhite,
    activeText: white,
    soft: white,
    medium: white,
  }),
] as const satisfies readonly FigmaTagColorRecipe[];

export const FIGMA_CIRCLE_TAG_CHIP_COLORS = [
  'red',
  'orange',
  'yellow',
  'green',
  'lightBlue',
  'darkBlue',
  'pink',
  'violet',
  'borderSubtle',
  'grey',
] as const satisfies readonly FigmaTagChipColor[];

export const FIGMA_SQUARE_TAG_CHIP_COLORS = [
  'grey',
  'green',
  'red',
  'orange',
  'yellow',
  'darkBlue',
  'violet',
  'pink',
  'lightBlue',
  'whiteOutlined',
] as const satisfies readonly FigmaTagChipColor[];

const tagRecipes = Object.fromEntries(
  FIGMA_TAG_CHIP_COLORS.map((tag) => {
    const visibleBorderColor =
      tag.color === 'whiteOutlined' || tag.color === 'borderSubtle' ? tokens.border.dark : tag.base;
    const plainBorderColor =
      tag.color === 'whiteOutlined' || tag.color === 'borderSubtle'
        ? tokens.border.dark
        : 'transparent';

    return [
      tag.color,
      {
        filled: {
          color: tag.activeText,
          backgroundColor: tag.base,
          borderColor: plainBorderColor,
          deleteIconColor: alpha(tag.activeText, 0.72),
          iconColor: tag.activeText,
          hoverBackgroundColor: tag.color === 'whiteOutlined' ? tokens.grey[100] : tag.base,
        },
        outlined: {
          color: tag.text,
          backgroundColor: tag.medium,
          borderColor: visibleBorderColor,
          deleteIconColor: tag.text,
          iconColor: tag.iconColor || tag.base,
          hoverBackgroundColor: tag.medium ?? tag.soft,
        },
        pill: {
          color: tag.text,
          backgroundColor: tag.medium,
          borderColor: plainBorderColor,
          deleteIconColor: alpha(tag.text, 0.65),
          iconColor: tag.iconColor || tag.base,
          hoverBackgroundColor: tag.soft,
        },
        pillBordered: {
          color: tag.text,
          backgroundColor: tag.medium,
          borderColor: visibleBorderColor,
          deleteIconColor: alpha(tag.text, 0.65),
          iconColor: tag.iconColor || tag.base,
          hoverBackgroundColor: tag.soft,
        },
        square: {
          color: tag.text,
          backgroundColor: tag.medium,
          borderColor: plainBorderColor,
          deleteIconColor: alpha(tag.text, 0.65),
          iconColor: tag.iconColor || tag.base,
          hoverBackgroundColor: tag.medium ?? tag.soft,
        },
        squareBordered: {
          color: tag.text,
          backgroundColor: tag.medium,
          borderColor: visibleBorderColor,
          deleteIconColor: alpha(tag.text, 0.65),
          iconColor: tag.iconColor || tag.base,
          hoverBackgroundColor: tag.medium ?? tag.soft,
        },
        squareActive: {
          color: tag.activeText,
          backgroundColor: tag.base,
          borderColor: plainBorderColor,
          deleteIconColor: alpha(tag.activeText, 0.72),
          iconColor: tag.activeText,
          hoverBackgroundColor:
            tag.color === 'whiteOutlined' ? tokens.grey[100] : alpha(tag.base, 0.9),
        },
      },
    ];
  }),
) as Partial<Record<ChipColorKey, ChipColorRecipe>>;

const semanticRecipes = {
  default: {
    filled: {
      color: tokens.text.primary,
      backgroundColor: tokens.grey[90],
      deleteIconColor: tokens.text.secondary,
      hoverBackgroundColor: tokens.grey[100],
    },
    outlined: {
      borderColor: tokens.border.main,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      hoverBackgroundColor: tokens.grey[100],
      deleteIconColor: tokens.text.secondary,
    },
  },
  primary: {
    filled: {
      color: white,
      backgroundColor: tokens.primary.darker,
      deleteIconColor: alpha(white, 0.8),
      hoverBackgroundColor: tokens.grey[30],
    },
    outlined: {
      borderColor: tokens.primary.main,
      color: tokens.primary.darker,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.primary.main, 0.08),
      deleteIconColor: tokens.primary.darker,
    },
  },
  secondary: {
    filled: {
      color: tokens.primary.darker,
      backgroundColor: tokens.primary.light,
      deleteIconColor: alpha(tokens.primary.darker, 0.6),
    },
    outlined: {
      borderColor: tokens.primary.main,
      color: tokens.primary.dark,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.primary.main, 0.08),
      deleteIconColor: tokens.primary.dark,
    },
  },
  info: {
    filled: {
      color: white,
      backgroundColor: tokens.primary.main,
      deleteIconColor: alpha(white, 0.75),
    },
    outlined: {
      borderColor: tokens.primary.main,
      color: tokens.primary.dark,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.primary.main, 0.08),
      deleteIconColor: tokens.primary.dark,
    },
  },
  error: {
    filled: {
      color: white,
      backgroundColor: tokens.error.main,
      deleteIconColor: alpha(white, 0.8),
    },
    outlined: {
      borderColor: tokens.error.main,
      color: tokens.error.dark,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.error.main, 0.08),
      deleteIconColor: tokens.error.dark,
    },
    pill: tagRecipes.red?.pill,
  },
  success: {
    filled: {
      color: white,
      backgroundColor: tokens.success.main,
      deleteIconColor: alpha(white, 0.8),
    },
    outlined: {
      borderColor: tokens.success.main,
      color: tokens.success.dark,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.success.main, 0.08),
      deleteIconColor: tokens.success.dark,
    },
    pill: tagRecipes.green?.pill,
  },
  warning: {
    filled: {
      color: tokens.warning.dark,
      backgroundColor: tokens.warning.light,
      deleteIconColor: alpha(tokens.warning.dark, 0.65),
    },
    outlined: {
      borderColor: tokens.warning.main,
      color: tokens.warning.dark,
      backgroundColor: 'transparent',
      hoverBackgroundColor: alpha(tokens.warning.main, 0.15),
      deleteIconColor: tokens.warning.dark,
    },
    pill: tagRecipes.yellow?.pill,
  },
  brand: {
    filled: {
      color: white,
      backgroundColor: tokens.primary.main,
      deleteIconColor: alpha(white, 0.75),
      hoverBackgroundColor: alpha(tokens.primary.main, 0.8),
    },
  },
  brandLight: {
    filled: {
      color: tokens.primary.darker,
      backgroundColor: tokens.primary.lighter,
      deleteIconColor: alpha(tokens.primary.darker, 0.6),
      hoverBackgroundColor: alpha(tokens.primary.lighter, 0.9),
    },
  },
  successLight: {
    filled: {
      color: tokens.success.dark,
      backgroundColor: tokens.success.light,
      deleteIconColor: alpha(tokens.success.dark, 0.6),
    },
  },
  orangeLight: {
    filled: {
      color: tokens.orange[150],
      backgroundColor: alpha(tokens.orange[50], 0.5),
      deleteIconColor: alpha(tokens.orange[150], 0.65),
      hoverBackgroundColor: tokens.orange[25],
    },
  },
} satisfies Partial<Record<ChipColorKey, ChipColorRecipe>>;

export const chipColorRecipes = {
  ...semanticRecipes,
  ...tagRecipes,
} satisfies Partial<Record<ChipColorKey, ChipColorRecipe>>;

const buildChipVariantStyle = (recipe: ChipVariantRecipe) => ({
  color: recipe.color,
  backgroundColor: recipe.backgroundColor,
  ...(recipe.border
    ? { border: recipe.border }
    : recipe.borderColor
      ? { border: '1px solid' }
      : {}),
  ...(recipe.borderColor ? { borderColor: recipe.borderColor } : {}),
  ...(recipe.boxShadow ? { boxShadow: recipe.boxShadow } : {}),
  // Hover feedback is reserved for chips that actually do something. MUI adds `MuiChip-clickable`
  // for `onClick`/`clickable` and `MuiChip-deletable` for `onDelete`; a chip with neither is a
  // static label (role tags like "Lead auditor", "HR", "Accounting") and must not light up on
  // hover, which reads as "click me" and then does nothing.
  ...(recipe.hoverBackgroundColor
    ? {
        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
          backgroundColor: recipe.hoverBackgroundColor,
        },
      }
    : {}),
  ...(recipe.deleteIconColor
    ? {
        '& .MuiChip-deleteIcon': {
          color: recipe.deleteIconColor,
        },
      }
    : {}),
  '& .MuiChip-icon': {
    color: recipe.iconColor ?? recipe.color,
  },
});

export const chipColorVariants = (
  Object.entries(chipColorRecipes) as Array<[ChipColorKey, ChipColorRecipe]>
).flatMap(([color, recipe]) => [
  {
    props: { color },
    style: buildChipVariantStyle(recipe.filled),
  },
  ...(recipe.outlined
    ? [
        {
          props: { variant: 'outlined' as ChipVariantKey, color },
          style: buildChipVariantStyle(recipe.outlined),
        },
      ]
    : []),
  ...(recipe.pill
    ? [
        {
          props: { variant: 'pill' as ChipVariantKey, color },
          style: buildChipVariantStyle(recipe.pill),
        },
      ]
    : []),
  ...(recipe.pillBordered
    ? [
        {
          props: { variant: 'pillBordered' as ChipVariantKey, color },
          style: buildChipVariantStyle(recipe.pillBordered),
        },
      ]
    : []),
  ...(recipe.square
    ? [
        {
          props: { variant: 'square' as ChipVariantKey, color },
          style: {
            ...buildChipVariantStyle(recipe.square),
            borderRadius: 4,
          },
        },
      ]
    : []),
  ...(recipe.squareBordered
    ? [
        {
          props: { variant: 'squareBordered' as ChipVariantKey, color },
          style: {
            ...buildChipVariantStyle(recipe.squareBordered),
            borderRadius: 4,
          },
        },
      ]
    : []),
  ...(recipe.squareActive
    ? [
        {
          props: { variant: 'squareActive' as ChipVariantKey, color },
          style: {
            ...buildChipVariantStyle(recipe.squareActive),
            borderRadius: 4,
          },
        },
      ]
    : []),
]);
